
import { supabase } from "@/integrations/supabase/client";
import { EmailTemplate } from "@/types/emailTemplates";

/**
 * Improved fetchTemplates function with better error handling and retry logic
 */
export async function fetchTemplates(page = 1, pageSize = 10): Promise<{ templates: EmailTemplate[], totalCount: number }> {
  // Calculate the range for pagination
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  // Maximum retry attempts
  const maxRetries = 2;
  let retryCount = 0;
  
  // Wrapping in retry logic
  const executeWithRetry = async (): Promise<{ templates: EmailTemplate[], totalCount: number }> => {
    try {
      console.log(`Fetching templates (page ${page}, pageSize ${pageSize})`);
      
      // Get count first (separate query to avoid COUNT(*) performance issues)
      const { count, error: countError } = await (supabase as any)
        .from('email_templates')
        .select('*', { count: 'exact', head: true });
      
      if (countError) {
        throw new Error(`Error counting templates: ${countError.message}`);
      }
      
      // Then get paginated data
      const { data, error } = await (supabase as any)
        .from('email_templates')
        .select('*')
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        throw new Error(`Error fetching templates: ${error.message}`);
      }

      console.log(`Fetched ${data?.length || 0} templates of ${count} total`);
      return {
        templates: data as EmailTemplate[],
        totalCount: count || 0
      };
    } catch (error: any) {
      console.error("Error in fetchTemplates:", error);
      
      // Implement retry logic for specific error types
      if (retryCount < maxRetries && 
          (error.message?.includes('resource') || error.message?.includes('timeout'))) {
        retryCount++;
        console.log(`Retrying fetchTemplates (${retryCount}/${maxRetries})...`);
        
        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        return executeWithRetry();
      }
      
      // Rethrow with additional context
      throw new Error(`Failed to fetch email templates: ${error.message}`);
    }
  };
  
  return executeWithRetry();
}
